import { CATEGORY_ICON_GROUPS } from "@/components/categories/categoryIcons";

/**
 * Regras de exibição e validação de ingredientes.
 * API (IngredientsDTO): { id: string, name, price: number, visible: 'S' | 'N' }
 * Banco: name até 50 caracteres; price NUMERIC(10,2).
 */

export const NAME_MIN = 2;
export const NAME_MAX = 50;
export const PRICE_MAX = 99999999.99;

/** Minúsculas e sem acento, para busca e comparação */
export const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/** Só 'N' oculta (registro sem o campo continua visível, como no banco) */
export const isVisible = (ingredient) =>
  String(ingredient?.visible ?? "S").trim().toUpperCase() !== "N";

export const getPrice = (ingredient) => {
  const n = Number(ingredient?.price);
  return Number.isFinite(n) ? n : 0;
};

/** "2,50" | "2.50" | "1.234,56" → 2.5 | 1234.56; inválido → NaN */
export const parsePrice = (text) => {
  let s = String(text ?? "").trim().replace(/\s|R\$/g, "");
  if (!s) return NaN;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  return /^\d+(\.\d+)?$/.test(s) ? Number(s) : NaN;
};

/** No máximo 2 casas (tolerância: 0.29 * 100 = 28.999… em ponto flutuante) */
export const hasTwoDecimals = (n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6;

/** 2.5 → "2,50" (valor do campo de preço) */
export const formatPriceInput = (n) =>
  Number.isFinite(n) ? n.toFixed(2).replace(".", ",") : "";

// Ingredientes comuns que não estão nos ícones de categoria
const INGREDIENT_ICONS = [
  ["🥓", "presunto mortadela calabresa linguica salame bacon peperoni pepperoni lombo"],
  ["🧀", "queijo mussarela muçarela prato parmesao provolone catupiry cheddar requeijao gorgonzola"],
  ["🍗", "frango peito"],
  ["🥩", "carne hamburguer picanha file"],
  ["🍟", "batata palha frita"],
  ["🌾", "farofa farrofa granola aveia"],
  ["🥫", "molho ketchup maionese mostarda barbecue"],
  ["🍫", "chocolate nutella brigadeiro"],
  ["🍯", "mel condensado"],
  ["🥚", "ovo ovos gema clara"],
];

const ICON_INDEX = [
  ...INGREDIENT_ICONS,
  ...CATEGORY_ICON_GROUPS.flatMap((group) => group.icons),
].map(([emoji, keywords]) => [emoji, new Set(normalize(keywords).split(/\s+/))]);

/**
 * Emoji pelo nome ("Queijo Prato" → 🧀). "Sem ..." (remoção de ingrediente)
 * vira 🚫. Sem correspondência → null (a tela mostra a inicial).
 */
export const getIngredientIcon = (name) => {
  const words = normalize(name).split(/[^a-z0-9]+/).filter(Boolean);
  if (words[0] === "sem") return "🚫";
  for (const word of words) {
    const hit = ICON_INDEX.find(([, keywords]) => keywords.has(word));
    if (hit) return hit[0];
  }
  return null;
};

/**
 * Valida o formulário. `others` = demais ingredientes (nome repetido).
 * Retorna { errors, payload } — payload só quando não há erros.
 */
export const validateIngredient = ({ name, price, visible }, others = []) => {
  const errors = {};
  const cleanName = String(name ?? "").trim().replace(/\s+/g, " ");
  const value = parsePrice(price);

  if (!cleanName) errors.name = "Informe o nome do ingrediente";
  else if (cleanName.length < NAME_MIN) errors.name = `Use ao menos ${NAME_MIN} caracteres`;
  else if (cleanName.length > NAME_MAX) errors.name = `Máximo de ${NAME_MAX} caracteres`;
  else if (others.some((i) => normalize(i.name) === normalize(cleanName)))
    errors.name = "Já existe um ingrediente com esse nome";

  if (String(price ?? "").trim() === "") errors.price = "Informe o preço (0 para gratuito)";
  else if (!Number.isFinite(value)) errors.price = "Preço inválido. Ex.: 2,50";
  else if (!hasTwoDecimals(value)) errors.price = "Use no máximo 2 casas decimais";
  else if (value > PRICE_MAX) errors.price = "Preço acima do permitido";

  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    payload: { name: cleanName, price: value, visible: visible ? "S" : "N" },
  };
};
